import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Hint,
  Text,
} from "@babylonlabs-io/core-ui";
import { AiOutlineMinus, AiOutlinePlus } from "react-icons/ai";

import iconHistory from "@/ui/common/assets/expansion-history.svg";
import iconRenew from "@/ui/common/assets/expansion-renew.svg";
import iconVerified from "@/ui/common/assets/expansion-verified.svg";
import { useExpansionHistoryService } from "@/ui/common/hooks/services/useExpansionHistoryService";
import { useVerifiedStakingExpansionService } from "@/ui/common/hooks/services/useVerifiedStakingExpansionService";
import { useAppState } from "@/ui/common/state";
import { useDelegationV2State } from "@/ui/common/state/DelegationV2State";
import { useStakingExpansionState } from "@/ui/common/state/StakingExpansionState";
import { StakingExpansionStep } from "@/ui/common/state/StakingExpansionTypes";
import { DelegationWithFP } from "@/ui/common/types/delegationsV2";

import { ExpansionButton } from "./ExpansionButton";

interface StakeExpansionSectionProps {
  delegation: DelegationWithFP;
  isPendingExpansion?: boolean;
}

export function StakeExpansionSection({
  delegation,
  isPendingExpansion = false,
}: StakeExpansionSectionProps) {
  const {
    goToStep,
    setFormData,
    processing,
    openExpansionHistoryModal,
    expansionDisabled,
  } = useStakingExpansionState();
  const { delegations } = useDelegationV2State();
  const { getHistoryCount } = useExpansionHistoryService();
  const {
    openVerifiedExpansionModalForDelegation,
    getVerifiedExpansionInfoForDelegation,
    hasVerifiedTimelockRenewal,
  } = useVerifiedStakingExpansionService();
  const { isLoading: isUTXOsLoading } = useAppState();

  /**
   * Handle renew staking term button click.
   * This allows users to renew the timelock without adding new BSN/FP pairs.
   */
  const handleRenewStakingTerm = () => {
    if (processing) {
      // Cannot start renewal: another operation in progress
      return;
    }

    if (isUTXOsLoading) {
      // Cannot start renewal: UTXOs are still loading
      return;
    }

    // Initialize expansion form data with current delegation and empty selectedBsnFps
    // This signals we're doing a renewal-only operation
    const renewalFormData = {
      originalDelegation: delegation,
      selectedBsnFps: {}, // Empty - no new BSN/FP pairs
      feeRate: 0,
      feeAmount: 0,
      stakingTimelock: 0, // Will be set during the renewal process
      isRenewalOnly: true, // Flag to indicate this is a renewal-only operation
    };

    setFormData(renewalFormData);
    // Go to renewal timelock modal to show the new staking term
    goToStep(StakingExpansionStep.RENEWAL_TIMELOCK);
  };

  /**
   * Handle expansion history button click.
   */
  const handleExpansionHistory = () => {
    if (expansionHistoryCount > 0) {
      openExpansionHistoryModal(delegation);
    }
  };

  /**
   * Handle verified expansion button click.
   */
  const handleVerifiedExpansion = () => {
    openVerifiedExpansionModalForDelegation(delegation);
  };

  // Calculate actual expansion history count
  const expansionHistoryCount = getHistoryCount(
    delegations,
    delegation.stakingTxHashHex,
  );

  // Get verified expansion info for this specific delegation
  const delegationVerifiedExpansionInfo = getVerifiedExpansionInfoForDelegation(
    delegation.stakingTxHashHex,
  );

  // Check if this delegation has a verified timelock renewal
  const hasVerifiedRenewal = hasVerifiedTimelockRenewal(
    delegation.stakingTxHashHex,
  );

  // Create the renew button once with conditional arrow hiding
  const renewButton = (
    <ExpansionButton
      Icon={iconRenew}
      text="Renew Staking Term"
      onClick={handleRenewStakingTerm}
      disabled={
        processing || isUTXOsLoading || isPendingExpansion || hasVerifiedRenewal
      }
      hideArrow={hasVerifiedRenewal}
    />
  );

  return (
    <div className="w-full">
      <Accordion
        disabled={expansionDisabled}
        className="rounded border border-secondary-strokeLight bg-surface"
      >
        <AccordionSummary
          className="p-4"
          renderIcon={(expanded) =>
            expanded ? (
              <AiOutlineMinus size={16} />
            ) : (
              <AiOutlinePlus size={16} />
            )
          }
          iconClassName="mr-4"
        >
          <Text variant="body1" className="font-medium text-accent-primary">
            Stake Extension
          </Text>
        </AccordionSummary>
        <AccordionDetails className="space-y-3 px-4 pb-4">
          <div className="flex w-full flex-col gap-4">
            {hasVerifiedRenewal ? (
              <Hint
                tooltip="A timelock renewal is already verified. Please check 'Verified Stake Extension' to complete it."
                placement="top"
              >
                {renewButton}
              </Hint>
            ) : (
              renewButton
            )}
            <ExpansionButton
              Icon={iconHistory}
              text="Extension History"
              counter={
                expansionHistoryCount > 0
                  ? `${expansionHistoryCount}`
                  : undefined
              }
              onClick={handleExpansionHistory}
              disabled={
                expansionHistoryCount === 0 || processing || isUTXOsLoading
              }
            />
            {delegationVerifiedExpansionInfo.hasVerifiedExpansions && (
              <ExpansionButton
                Icon={iconVerified}
                text="Verified Stake Extension"
                counter={`${delegationVerifiedExpansionInfo.count}`}
                onClick={handleVerifiedExpansion}
                disabled={
                  !delegationVerifiedExpansionInfo.hasVerifiedExpansions ||
                  processing ||
                  isUTXOsLoading
                }
              />
            )}
          </div>
        </AccordionDetails>
      </Accordion>
    </div>
  );
}
